// app/actions/chat-actions.ts
"use server";

import { prisma } from "@/lib/prisma";
import { revalidatePath } from "next/cache";
import { auth } from "@/lib/auth";
import { headers } from "next/headers";
import { authClient } from "@/lib/auth-client";

// Create a new chat (private or group)
export async function createChat(formData: {
  title: string;
  isGroup: boolean;
  userIds: bigint[]; // Array of user IDs to add to chat (INCLUDING current user)
}) {
  try {
    const session = await auth.api.getSession({
      headers: await headers(),
    });
    if (!session?.user?.id) {
      throw new Error("Unauthorized");
    }

    const currentUserId = BigInt(session.user.id);

    // Check if this is a private chat that already exists
    if (!formData.isGroup && formData.userIds.length === 2) {
      // For private chats, check if a chat already exists between these users
      const existingChat = await prisma.chats_titles.findFirst({
        where: {
          isgroup: 0,
          chat_members: {
            every: {
              chat_with: {
                in: formData.userIds
              }
            }
          }
        },
        include: {
          chat_members: true
        }
      });

      if (existingChat) {
        return { success: true, chatTitle: existingChat, alreadyExists: true };
      }
    }

    // Create chat title
    const chatTitle = await prisma.chats_titles.create({
      data: {
        title: formData.title,
        isgroup: formData.isGroup ? 1 : 0,
        image: null,
        initiated_at: new Date(),
        status: 0,
      },
    });

    // Add all users to chat members (including current user)
    // In your schema, each user needs their own row in chat_members
    for (const userId of formData.userIds) {
      await prisma.chat_members.create({
        data: {
          chat_with: userId,
          initiated_by: currentUserId, // The user who created the chat
          title_id: chatTitle.id,
          added_at: new Date(),
          isonline: 0,
          status: 0,
        },
      });
    }

    revalidatePath("/app/chat");
    return { success: true, chatTitle, alreadyExists: false };
  } catch (error) {
    console.error("Error creating chat:", error);
    return { success: false, error: "Failed to create chat" };
  }
}

// Get all chats for a user
// Debug function to check the database directly
export async function debugChats() {
  try {
    const session = await auth.api.getSession({
      headers: await headers(),
    });
    
    console.log("Session:", session?.user?.id);
    
    if (!session?.user?.id) {
      return { success: false, error: "No session" };
    }

    const userId = BigInt(session.user.id);
    console.log("User ID:", userId.toString());

    // Check if user exists
    const user = await prisma.users.findUnique({
      where: { id: userId }
    });
    console.log("User exists:", !!user);

    // Simple test - get all chat_members for this user
    const chatMembers = await prisma.chat_members.findMany({
      where: {
        chat_with: userId,
      },
      include: {
        chats_titles: true,
        users_chat_members_chat_withTousers: true,
      },
      take: 5,
    });

    console.log("Found chat members:", chatMembers.length);
    console.log("Chat members:", JSON.stringify(chatMembers, null, 2));

    return { 
      success: true, 
      userId: userId.toString(),
      chatMembers 
    };
  } catch (error) {
    console.error("Debug error:", error);
    return { success: false, error: String(error) };
  }
}

// Get all chats for a user - SIMPLIFIED VERSION
// app/actions/chat-actions.ts
export async function getUserChats(userId: bigint) {
  try {
    console.log("🔍 getUserChats for userId:", userId.toString());

    // Get chat memberships where user is EITHER chat_with OR initiated_by
    const chatMemberships = await prisma.chat_members.findMany({
      where: {
        OR: [
          { chat_with: userId, status: 0 },
          { initiated_by: userId, status: 0 },
        ],
        chats_titles: {
          status: 0
        }
      },
      include: {
        chats_titles: {
          include: {
            chat_members: {
              where: { status: 0 },
              include: {
                users_chat_members_chat_withTousers: {
                  select: {
                    id: true,
                    name: true,
                    email: true,
                    avatar: true,
                    org_name: true,
                    city: true,
                  },
                },
              },
            },
            chats: {
              take: 1,
              orderBy: {
                sent_at: "desc",
              },
              include: {
                users: {
                  select: {
                    id: true,
                    name: true,
                    email: true,
                    avatar: true,
                  },
                },
              },
            },
          },
        },
      },
      orderBy: {
        added_at: "desc",
      },
    });

    console.log("✅ Found chat memberships:", chatMemberships.length);

    // Get unique chats
    const uniqueChats = Array.from(
      new Map(
        chatMemberships.map(membership => [
          membership.chats_titles.id, 
          membership.chats_titles
        ])
      ).values()
    );

    console.log("📊 Total unique chats:", uniqueChats.length);

    return { 
      success: true, 
      chats: uniqueChats,
      debug: {
        membershipsFound: chatMemberships.length,
        uniqueChats: uniqueChats.length,
        firstMembership: chatMemberships[0] ? {
          chat_with: chatMemberships[0].chat_with.toString(),
          initiated_by: chatMemberships[0].initiated_by.toString(),
          title_id: chatMemberships[0].title_id,
        } : null
      }
    };
  } catch (error) {
    console.error("❌ Error in getUserChats:", error);
    return { success: false, error: String(error) };
  }
}
// Get chat messages
// In your actions.ts file
// Example check in app/app/chat/actions.ts
export async function getChatMessages(chatId: number) {
  try {
    const messages = await prisma.chats.findMany({
      where: { title_id: Number(chatId) }, // Force to number
      include: { users: true },
      orderBy: { sent_at: 'asc' }
    });
    return { success: true, messages };
  } catch (e) {
    return { success: false, error: String(e) };
  }
}
// Send a message
export async function sendMessage(formData: {
  chatId: number;
  text: string;
  attachments?: string;
  attachmentsType?: number;
}) {
  try {
    const session = await auth.api.getSession({
      headers: await headers(),
    });
    if (!session?.user?.id) {
      throw new Error("Unauthorized");
    }

    const userId = BigInt(session.user.id);

    const message = await prisma.chats.create({
      data: {
        sender: userId,
        title_id: formData.chatId,
        text: formData.text,
        attachements: formData.attachments || null,
        attachements_type: formData.attachmentsType || 0,
        isdisappearing: 1,
        disappear_time: "24h",
        sent_at: new Date(),
        deliverystatus: 1,
        status: 1,
      },
      include: {
        users: {
          select: {
            id: true,
            name: true,
            email: true,
            avatar: true,
          },
        },
        chats_titles: {
          select: {
            id: true,
            title: true,
            isgroup: true,
          },
        },
      },
    });

    revalidatePath("/app/chat");
    return { success: true, message };
  } catch (error) {
    console.error("Error sending message:", error);
    return { success: false, error: "Failed to send message" };
  }
}

// Search users for creating new chat
export async function searchUsers(query: string, currentUserId: bigint) {
  try {
    const users = await prisma.users.findMany({
      where: {
        AND: [
          {
            OR: [
              { name: { contains: query } },
              { email: { contains: query } },
              { org_name: { contains: query} },
            ],
          },
          { id: { not: currentUserId } },
          { deleted_at: null },
        ],
      },
      select: {
        id: true,
        name: true,
        email: true,
        avatar: true,
        org_name: true,
        city: true,
      },
      take: 10,
    });

    return { success: true, users };
  } catch (error) {
    console.error("Error searching users:", error);
    return { success: false, error: "Failed to search users" };
  }
}

// Get specific chat with participants
export async function getChatWithParticipants(chatId: number) {
  try {
    const chat = await prisma.chats_titles.findUnique({
      where: { id: chatId },
      include: {
        chat_members: {
          include: {
            users_chat_members_chat_withTousers: {
              select: {
                id: true,
                name: true,
                email: true,
                avatar: true,
              },
            },
          },
        },
        chats: {
          orderBy: {
            sent_at: "desc",
          },
          take: 1,
          include: {
            users: {
              select: {
                id: true,
                name: true,
              },
            },
          },
        },
      },
    });

    return { success: true, chat };
  } catch (error) {
    console.error("Error fetching chat:", error);
    return { success: false, error: "Failed to fetch chat" };
  }
}